import { useState, useEffect } from 'react';
import { useCreditsStore } from '../../stores/creditsStore';

interface CreditsDisplayProps {
  onPurchaseClick: () => void;
}

export default function CreditsDisplay({ onPurchaseClick }: CreditsDisplayProps) {
  const {
    creditsRemaining,
    freeRemaining,
    totalCredits,
    expiresAt,
    isLoading
  } = useCreditsStore();

  const [countdown, setCountdown] = useState<string>('--:--:--');

  // Update countdown every second
  useEffect(() => {
    if (!expiresAt) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = Math.max(0, expiry - now);

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-full animate-pulse">
        <div className="w-16 h-4 bg-white/10 rounded" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 animate-slide-up-fade">
      {/* Credits display */}
      <div className="flex items-center gap-3 px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-full">
        {/* Credit count with breakdown */}
        <div className="flex items-center gap-2">
          <span className="text-white/40 text-[10px] tracking-[0.2em] uppercase">Runs</span>
          <div className="flex items-center gap-1 font-mono text-sm tabular-nums">
            {freeRemaining > 0 && (
              <>
                <span className="text-[#22c55e] font-semibold">{freeRemaining}</span>
                {creditsRemaining > 0 && <span className="text-white/20">+</span>}
              </>
            )}
            {creditsRemaining > 0 && (
              <span className="text-white font-semibold">{creditsRemaining}</span>
            )}
            {totalCredits === 0 && (
              <span className="text-[#ef4444] font-semibold">0</span>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="w-px h-4 bg-white/10" />

        {/* Expiry countdown */}
        <div className="flex items-center gap-2">
          <span className="text-white/40 text-[10px] tracking-[0.2em] uppercase">Resets</span>
          <span className="font-mono text-xs text-white/60 tabular-nums">{countdown}</span>
        </div>
      </div>

      {/* Purchase button */}
      <button
        onClick={onPurchaseClick}
        disabled={totalCredits >= 10}
        className="group relative px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-full
                   hover:bg-white/[0.06] hover:border-white/[0.12] transition-all duration-200
                   disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <span className="text-[10px] tracking-[0.2em] uppercase text-white/60 group-hover:text-white/80 transition-colors">
          + Buy Runs
        </span>

        {/* Subtle glow on hover */}
        <div className="absolute inset-0 rounded-full bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity blur-sm" />
      </button>

      {/* Legend indicator (subtle) */}
      <div className="hidden sm:flex items-center gap-3 text-[10px] tracking-[0.15em] uppercase text-white/40">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#22c55e]" />
          <span>Free</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-white" />
          <span>Paid</span>
        </div>
      </div>
    </div>
  );
}
